import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useTasks } from '../stores/tasks.js';
import { useProjects } from '../stores/projects.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import type { Task } from '../api/tasks.js';
import type { Project } from '../api/projects.js';
import { BoardTaskRow } from '../components/BoardTaskRow.js';
import { TaskRow } from '../components/TaskRow.js';
import { ProjectHeading } from '../components/ProjectHeading.js';
import { ActionButtons } from '../components/ActionButtons.js';
import { WeekNav } from '../components/WeekNav.js';
import {
  todayInParis,
  addDays,
  dayOfWeekShort,
  dayOfMonth,
  isoWeekNumber,
  mondayOfWeek,
  formatWeekRange,
} from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { cn } from '../lib/cn.js';
import { agendaTasks } from '../lib/agenda-filter.js';

export function Board(): React.ReactElement {
  const tasks = useTasks((s) => s.tasks);
  const loading = useTasks((s) => s.loading);
  const load = useTasks((s) => s.load);
  const reschedule = useTasks((s) => s.reschedule);
  const projects = useProjects((s) => s.projects);
  const isMobile = useIsMobile();
  const [weekOffset, setWeekOffset] = useState(0);

  const today = todayInParis();
  const currentWeekMonday = mondayOfWeek(today);

  const days = useMemo(() => {
    const weekMonday = addDays(currentWeekMonday, weekOffset * 7);
    const weekSunday = addDays(weekMonday, 6);
    const start = weekOffset === 0 ? today : weekMonday;
    const out: string[] = [];
    let cursor = start;
    while (cursor <= weekSunday) {
      out.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return out;
  }, [currentWeekMonday, today, weekOffset]);

  // Filtre le rendu seulement : le drag-and-drop cherche dans la liste complète.
  const visible = useMemo(() => agendaTasks(tasks, projects, today), [tasks, projects, today]);

  const weekStart = days[0]!;
  const weekEnd = days[days.length - 1]!;
  const displayedMonday = addDays(currentWeekMonday, weekOffset * 7);
  const weekNum = isoWeekNumber(displayedMonday);

  useEffect(() => {
    load('range', { from: weekStart, to: weekEnd });
  }, [weekStart, weekEnd, load]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const dragged = draggedId ? tasks.find((t) => t.taskId === draggedId) : undefined;

  function onDragStart(e: DragStartEvent): void {
    setDraggedId(String(e.active.id));
  }

  function onDragEnd(e: DragEndEvent): void {
    setDraggedId(null);
    const taskId = String(e.active.id);
    const targetDay = e.over ? String(e.over.id) : null;
    if (!targetDay) return;
    const task = tasks.find((t) => t.taskId === taskId);
    if (!task || task.dueDate === targetDay) return;
    reschedule(taskId, targetDay);
  }

  const weekNav = (
    <WeekNav
      weekNum={weekNum}
      isCurrent={weekOffset === 0}
      onPrevious={() => setWeekOffset((w) => w - 1)}
      onCurrent={() => setWeekOffset(0)}
      onNext={() => setWeekOffset((w) => w + 1)}
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-col">
        <PageHeader
          title="Upcoming"
          surtitre={formatWeekRange(displayedMonday)}
          className="px-4 pt-5 pb-3"
        />
        <div className="sticky top-[calc(var(--barre-hauteur)+env(safe-area-inset-top))] z-10 h-12 bg-bg px-3 border-b border-edge flex items-center justify-center">
          {weekNav}
        </div>
        {loading ? (
          <div className="squelette m-4 h-64" />
        ) : (
          <div>
            {days.map((d) => (
              <MobileDaySection
                key={d}
                day={d}
                tasks={visible.filter((t) => t.dueDate === d)}
                projects={projects}
                isToday={d === today}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-6 py-8 h-full flex flex-col gap-6">
      <PageHeader
        title="Upcoming"
        surtitre={`${formatWeekRange(displayedMonday)} · week ${weekNum}`}
        actions={
          <>
            {weekNav}
            <ActionButtons />
          </>
        }
      />

      {loading ? (
        <div className="squelette h-64" />
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setDraggedId(null)}
        >
          {/* Les jours se partagent la largeur (Braise 2) : quatre colonnes de 14rem fixes laissaient
              la moitié droite d'un écran de bureau vide. Le plancher reste à 14rem : à 10rem, un
              lundi (sept colonnes) écrasait les cartes ; au-delà de la largeur, la grille défile. */}
          <div
            className="grid gap-3 flex-1 min-h-0 pb-2 overflow-x-auto"
            style={{ gridTemplateColumns: `repeat(${days.length}, minmax(14rem, 1fr))` }}
          >
            {days.map((d) => (
              <DayColumn
                key={d}
                day={d}
                tasks={visible.filter((t) => t.dueDate === d)}
                projects={projects}
                isToday={d === today}
              />
            ))}
          </div>
          {/* La colonne rogne ce qui en sort (`overflow-hidden`) : la tâche glissée voyage dans un
              calque rendu hors des colonnes. Pas d'animation de retour vers l'origine : la tâche
              rejoint sa nouvelle colonne à la réponse de l'API. Le calque est une image, les
              lecteurs d'écran n'y voient pas un second bouton « Complete ». */}
          <DragOverlay dropAnimation={null}>
            {dragged && (
              <div aria-hidden className="liste bg-surface-raised rounded-md shadow-lg cursor-grabbing">
                <TaskRow task={dragged} hideProject compact />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function MobileDaySection({
  day,
  tasks,
  projects,
  isToday,
}: {
  day: string;
  tasks: Task[];
  projects: Project[];
  isToday: boolean;
}): React.ReactElement {
  const groups = projects
    .map((p) => ({
      project: p,
      items: tasks.filter((t) => t.projectId === p.projectId),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <section>
      {/* Sous la barre de l'écran et la navigation de semaine (3rem), collantes elles aussi. */}
      <h2 className="sticky top-[calc(var(--barre-hauteur)+3rem+env(safe-area-inset-top))] z-5 px-4 py-1.5 bg-bg border-b border-edge">
        <DayLabel day={day} isToday={isToday} />
      </h2>
      {groups.length === 0 ? (
        <p className="discret px-6 py-4">No tasks</p>
      ) : (
        <div className="px-2 py-2 space-y-3">
          {groups.map((g) => (
            <div key={g.project.projectId}>
              <ProjectHeading as="h3" project={g.project} count={g.items.length} className="px-2 mb-0.5" />
              <div className="liste">
                {g.items.map((t) => (
                  <BoardTaskRow key={t.taskId} task={t} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DayColumn({
  day,
  tasks,
  projects,
  isToday,
}: {
  day: string;
  tasks: Task[];
  projects: Project[];
  isToday: boolean;
}): React.ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: day });

  const groups = projects
    .map((p) => ({
      project: p,
      items: tasks.filter((t) => t.projectId === p.projectId),
    }))
    .filter((g) => g.items.length > 0);

  return (
    // `.carte` du kit : aujourd'hui se dit `aria-current`, la colonne survolée pendant un
    // glisser prend la surface d'accent.
    <div
      ref={setNodeRef}
      aria-current={isToday ? 'true' : undefined}
      className={cn(
        'carte p-2 flex flex-col min-h-64 min-w-0 overflow-hidden transition-colors',
        isOver && 'bg-accent border-primary-ink'
      )}
    >
      <div className="px-1 py-1 shrink-0">
        <DayLabel day={day} isToday={isToday} />
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 mt-1">
        {groups.map((g) => (
          <section key={g.project.projectId}>
            <ProjectHeading as="h3" project={g.project} count={g.items.length} className="px-2 mb-0.5" />
            <div className="liste">
              {g.items.map((t) => (
                <BoardTaskRow key={t.taskId} task={t} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function DayLabel({ day, isToday }: { day: string; isToday: boolean }): React.ReactElement {
  return (
    <span className="flex items-baseline gap-2">
      <span className={cn('mention', isToday && 'text-now')}>{dayOfWeekShort(day)}</span>
      <span className={cn('chiffre text-sm', !isToday && 'text-muted')}>{dayOfMonth(day)}</span>
    </span>
  );
}
